import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { hasRole, useAuthOptional } from '../lib/auth';
import { fmt, label } from '../lib/format';
import { cell, heading, humanize, isId, STATUS } from '../lib/labels';
import { usePhone } from './phone';
import { CreditWall, parseShort } from './credit-wall';

// The building blocks every screen uses: one page shell, tables, detail lists, confirmed actions and forms.
// Accessibility (F18 FR4): real buttons and labels, dialogs that trap focus, visible focus rings, no colour-only state.

export function useApi<T = any>(path: string | null, opts: { refetchMs?: number } = {}) {
  return useQuery<T, ApiError>({ queryKey: [path], queryFn: () => api<T>(path!), enabled: Boolean(path), refetchInterval: opts.refetchMs, retry: (n, e) => e.status >= 500 && n < 2 });
}

export function Page({ title, prefix = 'Outlevo', actions, children }: { title: string; prefix?: string; actions?: ReactNode; children: ReactNode }) {
  useEffect(() => { document.title = `${prefix} · ${title}`; }, [title, prefix]);
  return (
    <main className="page" id="main" tabIndex={-1}>
      <header className="page-head"><h1>{title}</h1>{actions ? <div className="actions">{actions}</div> : null}</header>
      {children}
    </main>
  );
}

/** need: this section is what the person has to fill or choose now; it is coloured and tagged with why. */
export function Section({ title, children, actions, need, id }: { title: string; children: ReactNode; actions?: ReactNode; need?: string; id?: string }) {
  return (
    <section id={id} className={`section${need ? ' need' : ''}`} aria-label={title}>
      <div className="section-head"><h2>{title}</h2>{need ? <span className="need-tag">{need}</span> : null}{actions ? <div className="actions">{actions}</div> : null}</div>
      {children}
    </section>
  );
}

export function Loading() { return <p className="muted" role="status">Loading…</p>; }
/** doing: the action that failed ("approve", "save"); without it the box speaks of loading */
export function ErrorBox({ error, retry, doing }: { error: unknown; retry?: () => void; doing?: string }) {
  const e = error as ApiError;
  // out of credits (not a key's own spend cap): the way out is buttons, not a sentence; a plan limit (no sending mailboxes
  // on Free) gets the plan buttons. The server says who may pay and what each costs (ui/credit-wall.tsx).
  const quota = e?.code === 'QUOTA_EXCEEDED';
  const credits = quota && /credits needed/i.test(e.message ?? '');
  const gate = quota && !credits && (e.details?.gate === 'plan' || /\bplan (includes|allows)\b/i.test(e.message ?? ''));
  const refused = Boolean(e?.status && [400, 409, 422].includes(e.status));
  // the reason and the fix are two sentences ("…at least 300 are needed Qualify more accounts" ran on)
  const msg = e?.message && e?.fix && !credits && !/[.!?]$/.test(e.message.trim()) ? `${e.message.trim()}.` : e?.message;
  return (
    <div className="error" role="alert">
      {/* a refusal (400, 409, 422: a cap below the daily one, too few companies) is not a load failure (live 2026-11-01) */}
      <strong>{credits ? 'Not enough credits; nothing was spent.' : gate ? 'Not included in your plan; nothing was changed.' : e?.status === 404 ? 'Not found.' : doing ? `Couldn't ${doing}; nothing was changed.` : refused ? 'Not done; nothing was changed.' : 'This did not load; nothing was written.'}</strong> {msg}{e?.fix && !credits ? ` ${e.fix}` : ''}
      {credits ? <CreditWall need={parseShort(e.message)?.needed ?? null} /> : gate ? <CreditWall need={null} gate /> : null}
      {retry ? <> <button type="button" className="link" onClick={retry}>Try again</button></> : null}
    </div>
  );
}

/** Loads a path and renders its data, or the loading and error states. */
export function Load<T>({ path, children }: { path: string; children: (data: T) => ReactNode }) {
  const q = useApi<T>(path);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  return <>{children(q.data as T)}</>;
}

export function Status({ value }: { value: unknown }) {
  const v = String(value ?? '—');
  const tone = /^(approved|active|live|ready|executed|delivered|accepted|qualified|eligible|valid|ok|succeeded|sent|connected|built)/.test(v) ? 'good'
    : /^(blocked|failed|dead|rejected|disqualified|token_error|bounced|complained|revoked|expired|error|paused|stopped|invalid)/.test(v) ? 'bad' : 'neutral';
  return <span className={`chip chip-${tone}`}>{v === '—' ? v : (STATUS[v] ?? humanize(v)).toLowerCase()}</span>;
}

type Column<R> = { key: string; label?: string; render?: (row: R) => ReactNode };
/** How a row reads on a phone: the name and its status chip, then a muted line (the parts joined by " · "). */
export type PhoneRow<R> = { title: (row: R) => ReactNode; status?: (row: R) => ReactNode; meta?: (row: R) => Array<ReactNode | null | undefined | false> };
const STATUS_KEYS = /status|state|decision|approval|result$/;

export function Table<R extends Record<string, any>>({ rows, columns, rowLink, empty = 'Nothing here yet.', caption, phone }: {
  rows: R[]; columns?: Array<Column<R> | string>; rowLink?: (row: R) => string; empty?: string; caption?: string; phone?: PhoneRow<R>;
}) {
  const small = usePhone();
  if (!rows?.length) return <p className="muted">{empty}</p>;
  const cols: Column<R>[] = (columns ?? Object.keys(rows[0]!).filter((k) => k !== 'id' && !/_ids?$/.test(k)).slice(0, 8)).map((c) => (typeof c === 'string' ? { key: c } : c));
  if (phone && small) {
    // a list of two-line rows; the unlabelled columns (buttons) sit under the row
    const extras = cols.filter((c) => c.label === '' && c.render);
    return (
      <ul className="rows-list" aria-label={caption}>
        {rows.map((r, i) => {
          const meta = (phone.meta?.(r) ?? []).filter((x) => x !== null && x !== undefined && x !== false && x !== '');
          const body = <>
            <span className="rl-top"><b className="rl-title">{phone.title(r)}</b>{phone.status ? phone.status(r) : null}</span>
            {meta.length ? <span className="rl-meta">{meta.map((x, j) => <Fragment key={j}>{j ? ' · ' : ''}{x}</Fragment>)}</span> : null}
          </>;
          return (
            <li key={(r.id as string) ?? i}>
              {rowLink ? <Link className="rl-main" to={rowLink(r)}>{body}</Link> : <div className="rl-main">{body}</div>}
              {extras.length ? <div className="rl-extra">{extras.map((c) => <span key={c.key}>{c.render!(r)}</span>)}</div> : null}
            </li>
          );
        })}
      </ul>
    );
  }
  return (
    <div className="table-wrap">
      <table>
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        {/* a column without a heading (its buttons) is still named for screen readers */}
        <thead><tr>{cols.map((c) => <th key={c.key} scope="col">{(c.label ?? heading(c.key)) || <span className="sr-only">Actions</span>}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={(r.id as string) ?? i}>
              {cols.map((c, j) => {
                const content = c.render ? c.render(r) : STATUS_KEYS.test(c.key) ? <Status value={r[c.key]} /> : cell(c.key, r[c.key]) ?? fmt(r[c.key]);
                // a date never wraps onto several lines (it set the height of every row)
                const date = typeof r[c.key] === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(r[c.key] as string);
                return <td key={c.key} className={date ? 'nowrap' : undefined} data-label={(c.label ?? heading(c.key)) || 'Actions'}>{j === 0 && rowLink ? <Link to={rowLink(r)}>{content}</Link> : content}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function KV({ data, omit = [] }: { data: Record<string, unknown> | null | undefined; omit?: string[] }) {
  if (!data) return <p className="muted">—</p>;
  return (
    <dl className="kv">
      {Object.entries(data).filter(([k, v]) => !omit.includes(k) && k !== 'id' && !/_ids?$/.test(k) && !isId(v)).map(([k, v]) => (
        <div key={k}><dt>{heading(k)}</dt><dd>{STATUS_KEYS.test(k) && typeof v === 'string' ? <Status value={v} /> : cell(k, v) ?? fmt(v)}</dd></div>
      ))}
    </dl>
  );
}

export function Stat({ label: l, value, note }: { label: string; value: ReactNode; note?: string }) {
  return <div className="stat"><div className="stat-label">{l}</div><div className="stat-value">{value}</div>{note ? <div className="muted small">{note}</div> : null}</div>;
}

export function Dialog({ title, open, onClose, children }: { title: string; open: boolean; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal?.();
    if (!open && d.open) d.close?.();
  }, [open]);
  return (
    <dialog ref={ref} aria-labelledby={id} onClose={onClose} onCancel={onClose} className="dialog">
      <h2 id={id}>{title}</h2>
      {open ? children : null}
    </dialog>
  );
}

export type Field = { name: string; label: string; type?: 'text' | 'number' | 'email' | 'url' | 'date' | 'textarea' | 'select' | 'checkbox' | 'json'; options?: Array<string | { value: string; label: string }>; required?: boolean; placeholder?: string; initial?: unknown; help?: string; min?: number; max?: number; step?: number | 'any'; pattern?: string };

/** A button that opens a form, posts it, shows the API's error in place, and refreshes the screen on success. */
/** What a dangerous form changes, shown as a confirm step before it is sent (F25: the org's name and the change). */
export type ChangeSummary = { title?: string; rows: Array<[string, string]> };

/** A button that opens a form, posts it, shows the API's error in place, and refreshes the screen on success. */
export function ActionForm({ title, button, ariaLabel, method = 'POST', path, fields = [], confirm, confirmChange, onDone, transform, variant, check }: {
  title: string; button: string; /** the button's accessible name when its visible text is short ("Set caps" in a platform's row) */ ariaLabel?: string; method?: string; fields?: Field[]; confirm?: string; onDone?: (r: any) => void;
  /** Where it is sent; a function picks the path from the form (e.g. the metric a baseline is for). */
  path: string | ((v: Record<string, unknown>) => string);
  /** When set, submitting first shows this summary; only "Confirm" sends. */
  confirmChange?: (v: Record<string, unknown>) => ChangeSummary;
  transform?: (v: Record<string, unknown>) => unknown; variant?: 'danger' | 'primary';
  /** A mistake in the form, said before Review (a monthly cap below the daily one passed Review, live 2026-11-01). */
  check?: (v: Record<string, unknown>) => string | null;
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<Record<string, unknown> | null>(null);
  const [wrong, setWrong] = useState<string | null>(null);
  const qc = useQueryClient();
  const m = useMutation<any, ApiError, Record<string, unknown>>({
    mutationFn: (v) => api(typeof path === 'function' ? path(v) : path, { method, body: transform ? transform(v) : v }),
    onSuccess: (r) => { setOpen(false); setPending(null); void qc.invalidateQueries(); onDone?.(r); },
  });
  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (pending) { m.mutate(pending); return; }
    const data = new FormData(e.currentTarget);
    const v: Record<string, unknown> = {};
    for (const f of fields) {
      const raw = data.get(f.name);
      if (f.type === 'checkbox') v[f.name] = raw === 'on';
      else if (raw === null || raw === '') continue;
      else if (f.type === 'number') v[f.name] = Number(raw);
      else if (f.type === 'json') { try { v[f.name] = JSON.parse(String(raw)); } catch { v[f.name] = raw; } }
      else v[f.name] = raw;
    }
    const problem = check?.(v) ?? null;
    setWrong(problem);
    if (problem) return;
    if (confirmChange) { m.reset(); setPending(v); return; }
    m.mutate(v);
  };
  const summary = pending && confirmChange ? confirmChange(pending) : null;
  const close = () => { setOpen(false); setPending(null); };
  return (
    <>
      <button type="button" className={`btn ${variant ? `btn-${variant}` : ''}`} aria-label={ariaLabel} onClick={() => { m.reset(); setPending(null); setOpen(true); }}>{button}</button>
      <Dialog title={title} open={open} onClose={close}>
        <form onSubmit={submit} className="form">
          {confirm ? <p>{confirm}</p> : null}
          {summary ? (
            <div className="confirm-change" role="alert">
              <strong>{summary.title ?? 'Check the change before it is made'}</strong>
              <dl>{summary.rows.map(([k, v]) => <div key={k} style={{ display: 'contents' }}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
            </div>
          ) : null}
          <div className="form-fields" hidden={Boolean(summary)}>{fields.map((f) => <FieldInput key={f.name} f={f} />)}</div>
          {wrong ? <p className="error" role="alert">{wrong}</p> : null}
          {m.error ? <ErrorBox error={m.error} /> : null}
          <div className="form-actions">
            {summary ? <button type="button" className="btn" onClick={() => setPending(null)}>Back</button> : <button type="button" className="btn" onClick={close}>Cancel</button>}
            <button type="submit" className={`btn ${variant === 'danger' ? 'btn-danger' : 'btn-primary'}`} disabled={m.isPending}>{m.isPending ? 'Working…' : summary ? `Confirm: ${button}` : confirmChange ? 'Review' : button}</button>
          </div>
        </form>
      </Dialog>
    </>
  );
}

function FieldInput({ f }: { f: Field }) {
  const id = useId();
  const common = { id, name: f.name, required: f.required, placeholder: f.placeholder, min: f.min, max: f.max, step: f.step, pattern: f.pattern, defaultValue: f.initial === undefined ? undefined : typeof f.initial === 'object' ? JSON.stringify(f.initial, null, 2) : String(f.initial), 'aria-describedby': f.help ? `${id}-help` : undefined };
  return (
    <div className={`field ${f.type === 'checkbox' ? 'field-check' : ''}`}>
      <label htmlFor={id}>{f.label}</label>
      {f.type === 'textarea' || f.type === 'json' ? <textarea rows={f.type === 'json' ? 8 : 4} {...common} />
        : f.type === 'select' ? <select {...common}>{f.options!.map((o) => (typeof o === 'string' ? <option key={o} value={o}>{o.replace(/_/g, ' ')}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}</select>
        : f.type === 'checkbox' ? <input type="checkbox" id={id} name={f.name} defaultChecked={Boolean(f.initial)} />
        : <input type={f.type ?? 'text'} {...common} />}
      {f.help ? <div id={`${id}-help`} className="muted small">{f.help}</div> : null}
    </div>
  );
}

/** A one-click action with a confirmation step and inline errors. */
export function ActionButton({ path, method = 'POST', body, label: l, ariaLabel, confirm, variant, onDone, disabled, title }: {
  path: string; method?: string; body?: unknown; label: string; ariaLabel?: string; confirm?: string; variant?: 'danger' | 'primary'; onDone?: (r: any) => void; disabled?: boolean; title?: string;
}) {
  const qc = useQueryClient();
  const [asking, setAsking] = useState(false);
  // "Working…" stays until the page has read the new state: the old row came back for ~5 s after Yes (run 16)
  const m = useMutation<any, ApiError>({ mutationFn: () => api(path, { method, body }), onSuccess: async (r) => { setAsking(false); onDone?.(r); await qc.invalidateQueries(); } });
  return (
    <span className="action">
      {asking ? (
        <span role="group" aria-label={confirm}>
          <span className="small">{confirm}</span>{' '}
          <button type="button" className={`btn ${variant === 'danger' ? 'btn-danger' : 'btn-primary'}`} onClick={() => m.mutate()} disabled={m.isPending}>Yes</button>{' '}
          <button type="button" className="btn" onClick={() => setAsking(false)}>No</button>
        </span>
      ) : (
        <button type="button" className={`btn ${variant ? `btn-${variant}` : ''}`} disabled={disabled || m.isPending} title={title} aria-label={ariaLabel} onClick={() => (confirm ? setAsking(true) : m.mutate())}>{m.isPending ? 'Working…' : l}</button>
      )}
      {m.error ? <ErrorBox error={m.error} /> : null}
    </span>
  );
}

/** label: what the tabs choose, so two rows of tabs on one page are distinct landmarks ("Tabs", "Period"). */
export function Tabs({ tabs, label: name = 'Tabs' }: { tabs: Array<{ to: string; label: string; active: boolean }>; label?: string }) {
  // plain links (F25: tabs that differ only by query string must repaint)
  return <nav className="tabs" aria-label={name}>{tabs.map((t) => <a key={t.to} href={t.to} aria-current={t.active ? 'page' : undefined} className={t.active ? 'tab active' : 'tab'}>{t.label}</a>)}</nav>;
}
